/**
 * 前端页面 Mock 入口。
 *
 * 覆盖的页面：
 *   1. /cockpit/electricity       —— "电力驾驶舱"（老页面，走 /power-analysis 下的综合监控接口）
 *   2. /electricityHome           —— "驾驶舱-电"（大屏页，走 /power-analysis/cockpitel/* 接口）
 *
 * 仅在 NODE_ENV === 'development' 时由 src/main.js 引入。
 * 使用 Mock.js 在 XHR 层拦截，axios / 拦截器无需任何改动，业务拿到的仍然是 res.data。
 */

const Mock = require('mockjs')
const Random = Mock.Random

// -----------------------------------------------------------------------------
// 常量池
// -----------------------------------------------------------------------------
const COMPANY_POOL = [
  '华北分中心',
  '华东分中心',
  '华南分中心',
  '华中分中心',
  '西南分中心',
  '西北分中心',
  '东北分中心'
]
const PROPERTY_POOL = [
  '国家电网',
  '南方电网',
  '国电投新能源',
  '华能新能源',
  '大唐集团',
  '华电集团',
  '三峡集团'
]
const STATION_SUFFIX = ['变电站', '配电站', '开关站', '中控站']
const STATION_CITY = [
  '北京',
  '上海',
  '广州',
  '深圳',
  '成都',
  '西安',
  '武汉',
  '南京',
  '杭州',
  '青岛'
]
const FORM_DISTRIBUTION = ['箱变（强电间）', '户内开关柜', '箱式变压器', '环网柜']

// 京津冀地图上显示合理的坐标范围
const LON_RANGE = [114.5, 119.2]
const LAT_RANGE = [37.2, 41.5]

function randPick(list) {
  return list[Math.floor(Math.random() * list.length)]
}

function fixed(n, digits = 2) {
  return Number(Number(n).toFixed(digits))
}

function randFloat(min, max, digits = 2) {
  return fixed(min + Math.random() * (max - min), digits)
}

// -----------------------------------------------------------------------------
// 下面这一组是两个页面共用的接口（/monitors/stations、/monitors/details 等）
// -----------------------------------------------------------------------------

// 场站弹窗：分中心 / 产权单位 / 场站列表
function buildStationDialog() {
  const companyInfoList = COMPANY_POOL.map((name, idx) => ({
    id: `COMP-${String(idx + 1).padStart(3, '0')}`,
    companyName: name
  }))

  const propertyUnits = PROPERTY_POOL.slice()

  const stationInfos = Array.from({ length: 10 }).map((_, idx) => {
    const city = randPick(STATION_CITY)
    const suffix = randPick(STATION_SUFFIX)
    return {
      id: `ST-${String(idx + 1).padStart(4, '0')}`,
      stationName: `${city}${suffix}${idx + 1}号站`,
      stationNumber: `SN${100000 + idx + 1}`,
      // 电力大屏地图按京津冀渲染，这里给每个场站一个落在范围内的经纬度
      longitude: randFloat(LON_RANGE[0], LON_RANGE[1], 4),
      latitude: randFloat(LAT_RANGE[0], LAT_RANGE[1], 4)
    }
  })

  return {
    code: 200,
    msg: 'success',
    data: {
      companyInfoList,
      propertyUnits,
      stationInfos
    }
  }
}

// 场站详情：同时覆盖老电力驾驶舱与驾驶舱-电的弹窗
function buildStationDetail() {
  const city = randPick(STATION_CITY)
  const suffix = randPick(STATION_SUFFIX)
  return {
    code: 200,
    msg: 'success',
    data: {
      stationName: `${city}${suffix}1号站`,
      companyName: randPick(COMPANY_POOL),
      propertyName: randPick(PROPERTY_POOL),
      stationNumber: `SN${100001}`,
      equipmentCount: Random.integer(30, 120),
      yearPower: randFloat(3, 18, 2),
      monthlyElectricityQuantity: randFloat(12000, 68000, 2),
      yesterdayElectricity: randFloat(400, 3800, 2),
      coverArea: Random.integer(800, 3500),
      address: `${city}市${Random.cword(2, 4)}区${Random.cword(2, 4)}路${Random.integer(1, 999)}号`,
      stationFile: ''
    }
  }
}

// -----------------------------------------------------------------------------
// /cockpit/electricity —— 老电力驾驶舱用到的接口（保持原行为）
// -----------------------------------------------------------------------------

function buildStationAlarm() {
  return {
    code: 200,
    msg: 'success',
    data: [
      { level_name: '特大告警', totalAlarm: Random.integer(0, 3) },
      { level_name: '重大告警', totalAlarm: Random.integer(0, 6) },
      { level_name: '严重告警', totalAlarm: Random.integer(2, 15) },
      { level_name: '一般告警', totalAlarm: Random.integer(5, 40) }
    ]
  }
}

function buildElectricityUsage() {
  return {
    code: 200,
    msg: 'success',
    data: {
      dayElectricityConsumption: randFloat(800, 3200, 2),
      monthElectricityConsumption: randFloat(120, 640, 2),
      monthElectricityConsumptionRingRatio: randFloat(-15, 15, 2)
    }
  }
}

function buildStationOverview() {
  return {
    code: 200,
    msg: 'success',
    data: {
      numberTransformers: Random.integer(2, 8),
      levelVoltage: randPick(['10 / 0.4', '35 / 10', '20 / 0.4']),
      ratedCapacity: `${Random.integer(8, 40) * 100}`,
      formDistribution: randPick(FORM_DISTRIBUTION)
    }
  }
}

function buildStationParam() {
  const count = Random.integer(2, 3)
  const list = Array.from({ length: count }).map(() => ({
    activePower: randFloat(120, 980, 2),
    reactivePower: randFloat(20, 260, 2),
    apparentPower: randFloat(140, 1020, 2),
    temperatureTransformer: randFloat(28, 72, 1),
    loadTransformer: randFloat(25, 92, 1)
  }))
  return { code: 200, msg: 'success', data: list }
}

function buildDeviceStatistics() {
  return {
    code: 200,
    msg: 'success',
    data: {
      distributionChart: Random.integer(3, 12),
      shexiangtou: Random.integer(8, 32),
      bianyaqi: Random.integer(2, 8),
      gaoyace: Random.integer(3, 14),
      diyace: Random.integer(4, 18),
      zhiliuping: Random.integer(1, 6)
    }
  }
}

function buildStationEnv() {
  return {
    yanwu: randPick(['无', '无', '无', '有']),
    shuijin: randPick(['无', '无', '无', '有']),
    zaoshengfenbei: randFloat(32, 68, 1),
    wendu: randFloat(18, 32, 1),
    shidu: randFloat(35, 72, 1)
  }
}

function buildPowerStatistics() {
  const obj = {}
  for (let h = 0; h < 24; h += 2) {
    const hh = String(h).padStart(2, '0')
    const base = 60 + 40 * Math.sin(((h - 6) / 24) * Math.PI * 2)
    const v = Math.max(20, base + (Math.random() * 30 - 15))
    obj[`${hh}:00`] = fixed(v, 2)
  }
  return [obj]
}

// -----------------------------------------------------------------------------
// /electricityHome —— 驾驶舱-电（大屏）用到的 /cockpitel/* 接口
// -----------------------------------------------------------------------------

// 告警情况统计：页面根据 level_name 分发到 specialAlarm / majorAlarm / seriousAlarm / averageAlarm，取 alarmCount
function buildCockpitElAlarm() {
  return {
    code: 200,
    msg: 'success',
    data: [
      { level_name: '特大告警', alarmCount: Random.integer(0, 3) },
      { level_name: '重大告警', alarmCount: Random.integer(1, 6) },
      { level_name: '严重告警', alarmCount: Random.integer(3, 18) },
      { level_name: '一般告警', alarmCount: Random.integer(8, 45) }
    ]
  }
}

// 24 小时实时负载：data 是长度为 1 的数组，里面对象 key 是时刻，value 是负载
function buildCockpitElLoad() {
  const obj = {}
  for (let h = 0; h < 24; h += 1) {
    const hh = String(h).padStart(2, '0')
    // 白天高、夜晚低的模拟曲线
    const base = 1800 + 1200 * Math.sin(((h - 6) / 24) * Math.PI * 2)
    const v = Math.max(400, base + (Math.random() * 500 - 250))
    obj[`${hh}:00`] = fixed(v, 2)
  }
  return { code: 200, msg: 'success', data: [obj] }
}

// 计划停电情况统计
function buildCockpitElPowerCut() {
  const onInPlan = Random.integer(1, 6)
  const noStartePlan = Random.integer(0, 5)
  const terminationPlan = Random.integer(0, 3)
  const planPowerOutages = onInPlan * 3 + noStartePlan * 2 + terminationPlan
  return {
    code: 200,
    msg: 'success',
    data: {
      planPowerOutages,
      onInPlan,
      noStartePlan,
      terminationPlan
    }
  }
}

// 电能质量：近 30 天的视在/有功/无功
function buildCockpitElQuality() {
  const now = new Date()
  const activePower = []
  const apparentPower = []
  const reactivePower = []
  for (let i = 29; i >= 0; i -= 1) {
    const d = new Date(now.getTime() - i * 24 * 3600 * 1000)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const nowTime = `${y}-${m}-${day} 00:00:00`

    const act = randFloat(1800, 4200, 2)
    const rea = randFloat(200, 900, 2)
    const app = fixed(Math.sqrt(act * act + rea * rea), 2)
    activePower.push({ nowTime, activePower: act })
    reactivePower.push({ nowTime, reactivePower: rea })
    apparentPower.push({ nowTime, apparentPower: app })
  }
  return {
    code: 200,
    msg: 'success',
    data: { activePower, apparentPower, reactivePower }
  }
}

// 实时数据：场站/变压器总数、当前任务/计划、完成率
function buildCockpitElReal() {
  const currentTask = Random.integer(18, 56)
  const currentPlan = Random.integer(12, currentTask)
  const rate = fixed((currentPlan / currentTask) * 100, 1)
  return {
    code: 200,
    msg: 'success',
    data: {
      stationNumber: Random.integer(58, 128),
      transformerTotal: Random.integer(120, 360),
      currentTask,
      currentPlan,
      taskRateofCompletion: rate
    }
  }
}

// 抢修/维修任务：data[0] 抢修，data[1] 维修
function buildCockpitElTask() {
  const makeOne = () => {
    const taskCompleted = Random.integer(8, 30)
    const taskUnfinished = Random.integer(1, 12)
    return {
      taskCompleted,
      taskUnfinished,
      taskTotal: taskCompleted + taskUnfinished
    }
  }
  return {
    code: 200,
    msg: 'success',
    data: [makeOne(), makeOne()]
  }
}

// 总用电量（GWh）：页面拿到 res 后直接 res.toFixed(3) —— 所以 body 就是一个数值
function buildCockpitElTotal() {
  return randFloat(120, 780, 3)
}

// 近七日用电趋势
function buildCockpitElTrend() {
  const now = new Date()
  const data = []
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(now.getTime() - i * 24 * 3600 * 1000)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    data.push({
      nowTime: `${y}-${m}-${day} 00:00:00`,
      onceEpaddIns: randFloat(2800, 6200, 2)
    })
  }
  return { code: 200, msg: 'success', data }
}

// 各中心新旧设备占比
function buildCockpitElEquipment() {
  return {
    code: 200,
    msg: 'success',
    data: COMPANY_POOL.map((companyName) => ({
      companyName,
      before2012: Random.integer(5, 40),
      between2012And2019: Random.integer(20, 80),
      after2019: Random.integer(30, 120)
    }))
  }
}

// 尖峰平谷：取当月（或 URL 上带的 month）的每一天
function buildCockpitElPeak(options) {
  let month = ''
  try {
    const url = options && options.url ? options.url : ''
    const m = url.match(/[?&]month=([^&]+)/)
    month = m ? decodeURIComponent(m[1]) : ''
  } catch (e) {
    month = ''
  }

  const now = new Date()
  let year = now.getFullYear()
  let mon = now.getMonth() + 1
  if (/^\d{4}-\d{1,2}$/.test(month)) {
    const parts = month.split('-')
    year = Number(parts[0])
    mon = Number(parts[1])
  }
  const daysInMonth = new Date(year, mon, 0).getDate()

  const tipPower = []
  const peakPower = []
  const flatPower = []
  const valleyPower = []
  for (let d = 1; d <= daysInMonth; d += 1) {
    const mm = String(mon).padStart(2, '0')
    const dd = String(d).padStart(2, '0')
    const nowTime = `${year}-${mm}-${dd} 00:00:00`
    tipPower.push({ nowTime, onceEpaddIns: randFloat(120, 260, 2) })
    peakPower.push({ nowTime, onceEpaddIns: randFloat(300, 620, 2) })
    flatPower.push({ nowTime, onceEpaddIns: randFloat(220, 480, 2) })
    valleyPower.push({ nowTime, onceEpaddIns: randFloat(80, 220, 2) })
  }

  return {
    code: 200,
    msg: 'success',
    data: { tipPower, peakPower, flatPower, valleyPower }
  }
}

// 天气接口：page.tq = res.data[0].sdtqInfos[0]
function buildWeatherHour() {
  return {
    code: 200,
    msg: 'success',
    data: [
      {
        sdtqInfos: [
          {
            tqxx: randPick(['晴', '多云', '阴', '小雨']),
            fxms: randPick(['东南风 1 级', '东北风 2 级', '西南风 1 级', '北风 3 级']),
            kqzl: randPick(['优', '良', '轻度'])
          }
        ]
      }
    ]
  }
}

// -----------------------------------------------------------------------------
// 注册拦截
// -----------------------------------------------------------------------------

function registerMocks() {
  // -------- /cockpit/electricity 老页面（保留） ---------------------------
  Mock.mock(/\/power-analysis\/monitors\/stations(\?.*)?$/, 'get', buildStationDialog)
  Mock.mock(/\/power-analysis\/home\/page\/alarm(\?.*)?$/, 'get', buildStationAlarm)
  Mock.mock(/\/power-analysis\/home\/page\/electricity(\?.*)?$/, 'get', buildElectricityUsage)
  Mock.mock(/\/power-analysis\/home\/page\/overview(\?.*)?$/, 'get', buildStationOverview)
  Mock.mock(/\/power-analysis\/home\/page\/parameters(\?.*)?$/, 'get', buildStationParam)
  Mock.mock(/\/power-analysis\/home\/page\/statistics(\?.*)?$/, 'get', buildDeviceStatistics)
  Mock.mock(/\/power-analysis\/monitors\/details(\?.*)?$/, 'get', buildStationDetail)
  Mock.mock(/\/power-analysis\/monitor\/station\/id(\?.*)?$/, 'get', buildStationEnv)
  Mock.mock(/\/power-analysis\/monitors\/power\/statistics(\?.*)?$/, 'get', buildPowerStatistics)

  // -------- /electricityHome 大屏页 ---------------------------------------
  Mock.mock(/\/power-analysis\/cockpitel\/alarm(\?.*)?$/, 'get', buildCockpitElAlarm)
  Mock.mock(/\/power-analysis\/cockpitel\/load(\?.*)?$/, 'get', buildCockpitElLoad)
  Mock.mock(/\/power-analysis\/cockpitel\/power\/plan(\?.*)?$/, 'get', buildCockpitElPowerCut)
  Mock.mock(/\/power-analysis\/cockpitel\/quality(\?.*)?$/, 'get', buildCockpitElQuality)
  Mock.mock(/\/power-analysis\/cockpitel\/real(\?.*)?$/, 'get', buildCockpitElReal)
  Mock.mock(/\/power-analysis\/cockpitel\/task(\?.*)?$/, 'get', buildCockpitElTask)
  Mock.mock(
    /\/power-analysis\/cockpitel\/total\/electricity(\?.*)?$/,
    'get',
    buildCockpitElTotal
  )
  Mock.mock(/\/power-analysis\/cockpitel\/trend(\?.*)?$/, 'get', buildCockpitElTrend)
  Mock.mock(/\/power-analysis\/cockpitel\/equipment(\?.*)?$/, 'get', buildCockpitElEquipment)
  // peak 接口要用到 URL 上的 month 参数，所以这里拿 options
  Mock.mock(/\/power-analysis\/cockpitel\/peak(\?.*)?$/, 'get', buildCockpitElPeak)

  // 天气
  Mock.mock(
    /\/power-system\/weatherController\/getWeatherHourData(\?.*)?$/,
    'post',
    buildWeatherHour
  )

  // eslint-disable-next-line no-console
  console.info(
    '[mock] electricity & cockpit-electricity mocks registered (19 endpoints)'
  )
}

registerMocks()
